import { useEffect } from 'react'

import { useSetState } from '@domain-group/react-hooks'

import { Button, Form, SidebarModal } from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'GenerateReportModal'

type DefaultState = {
  end_date?: string,
  reportKey: 'template_rejections' | 'first_time_right_leaderboard' | 'active_projects' | '',
  start_date?: string,
}

const defaultState: DefaultState = {
  end_date: '',
  reportKey: '',
  start_date: '',
}

type ModalCallbacks = {
  closeModal: () => void,
  generateTeamPerformanceReport: (payload: HandleCallbackActionParams) => void,
  updateReportFormState: (formState: DefaultState) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  formState: DefaultState,
  generateReport: (payload?: any) => Promise<any>,
  loading: boolean,
  saveEnabled: boolean,
  showModal: boolean,
}

const GenerateReportModal = (props: ModalProps) => {
  const {
    callbacks,
    formState,
    generateReport,
    loading,
    saveEnabled,
    showModal,
  } = props
  const { closeModal, generateTeamPerformanceReport, updateReportFormState } = callbacks

  const [state, setState] = useSetState(formState)
  const { end_date, start_date, reportKey } = state

  // Sync local state changes to parent
  useEffect(() => {
    updateReportFormState(state)
  }, [reportKey, end_date, start_date])

  // Reset local state when modal opens
  useEffect(() => {
    if (showModal){
      setState(defaultState)
    }
  }, [showModal])

  const generateReportPayload = {
    callbacks: {
      action: generateReport as any,
      afterAction: () => closeModal(),
    },
    toastText: 'Report Queued',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Team Performance" titleSecondLine="Report" />

      <SidebarModal.Content>
        <Form.Field label="* Report Type">
          <select
            name="report_type"
            value={reportKey}
            onChange={e => setState({ reportKey: e.target.value as DefaultState['reportKey'] })}
          >
            <option value="">Please Select...</option>
            <option value="first_time_right_leaderboard">Task Accuracy Leaderboard</option>
            <option value="template_rejections">Template Rejections by Client QC</option>
            <option value="active_projects">Active Projects</option>
          </select>
        </Form.Field>

        {reportKey !== 'active_projects' && (
          <Form.Row>
            <Form.Field label="Start Date" marginTop="large">
              <input
                name="start_date"
                onChange={e => setState({ start_date: e.target.value })}
                type="date"
                value={start_date}
              />
            </Form.Field>

            <Form.Field label="End Date" marginTop="large">
              <input
                disabled={!start_date}
                name="end_date"
                onChange={e => setState({ end_date: e.target.value })}
                type="date"
                value={end_date}
                min={start_date}
              />
            </Form.Field>
          </Form.Row>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={loading}
          loadingBubbleColor="white"
          onClick={() => generateTeamPerformanceReport(generateReportPayload)}
          size="large"
        >
          {saveEnabled ? 'Download Report' : 'Complete Fields'}
        </Button>

        <Button buttonStyle="secondaryUtility" onClick={() => closeModal()} size="large">
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  // @ts-expect-error RenderController type issue
  <SidebarModal.RenderController {...props}>
    <GenerateReportModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
